import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {useState} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, IconButton} from '@primer/react'
import type {Repository} from '@github-ui/current-repository'
import {NewAgentTaskForm} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {TOGGLE_COPILOT_OVERLAY} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
import {useCreateTask} from '@github-ui/agent-sessions/hooks/useCreateTask'
import {useTasks} from '@github-ui/agent-sessions/hooks/useTasks'
import type {CreateTaskReference} from '@github-ui/agent-sessions/components/NewTaskChatInput'

export type NewAgentTaskButtonProps = {
  isIconButton?: boolean
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  hideRepoSelector?: boolean
  hideIcon?: boolean
  eventType: string
  size?: 'small' | 'medium' | 'large'
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
}

export {NewAgentTaskButton}

function NewAgentTaskButton({
  isIconButton,
  currentRepo,
  currentReference,
  hideRepoSelector,
  eventType,
  size,
  tooltipDirection,
}: NewAgentTaskButtonProps) {
  const [open, setOpen] = useState(false)
  return (
    <AnchoredOverlay
      width="large"
      align="end"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => {
        return isIconButton ? (
          <IconButton
            icon={AgentIcon}
            aria-label="New agent task"
            // We shouldn't need this but the typing requires something
            aria-labelledby={undefined}
            size={size}
            tooltipDirection={tooltipDirection}
            {...props}
          />
        ) : (
          <Button {...props} leadingVisual={<AgentIcon />} size={size}>
            New agent task
          </Button>
        )
      }}
    >
      <NewAgentTaskButtonContent
        currentRepo={currentRepo}
        currentReference={currentReference}
        hideRepoSelector={hideRepoSelector}
        setOpen={setOpen}
        eventType={eventType}
      />
    </AnchoredOverlay>
  )
}

type NewAgentTaskButtonContentProps = {
  currentRepo?: Repository
  currentReference?: CreateTaskReference
  hideRepoSelector?: boolean
  setOpen: (open: boolean) => void
  eventType: string
}

export function NewAgentTaskButtonContent(props: NewAgentTaskButtonContentProps) {
  return (
    <ConsecutiveCAPI401sProvider>
      <InnerNewAgentTaskButtonContent {...props} />
    </ConsecutiveCAPI401sProvider>
  )
}

export function InnerNewAgentTaskButtonContent({
  currentRepo,
  currentReference,
  hideRepoSelector,
  setOpen,
  eventType,
}: NewAgentTaskButtonContentProps) {
  // Warm up the cache by prefetching tasks
  // TODO: Consider using something inside useCreateTask to prefetch tasks instead
  // e.g await queryClient.refetchQueries({ queryKey: ['posts'], type: 'active' })
  useTasks({sessionsPollingInterval: false})
  const {createTask} = useCreateTask({})

  return (
    <NewAgentTaskForm
      createTask={createTask}
      currentRepo={currentRepo}
      currentReference={currentReference}
      onCreate={() => {
        setOpen(false)
        document.dispatchEvent(new CustomEvent(TOGGLE_COPILOT_OVERLAY))
      }}
      hideRepoSelector={hideRepoSelector}
      eventType={eventType}
    />
  )
}

try{ NewAgentTaskButton.displayName ||= 'NewAgentTaskButton' } catch {}
try{ NewAgentTaskButtonContent.displayName ||= 'NewAgentTaskButtonContent' } catch {}
try{ InnerNewAgentTaskButtonContent.displayName ||= 'InnerNewAgentTaskButtonContent' } catch {}